import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/tr';
import { KTSVG } from 'src/_metronic/helpers';
import usePermission from 'src/app/permissions/use-permission';
import { useProductsOnTheWayList } from '../products-on-the-way/service';

const ITEMS_PER_PAGE = 5;

const ProductsOnTheWayWidget: React.FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [permLoading, hasPermission] = usePermission('ROLE_CRM_PRODUCT_PRODUCTSONTHEWAY');

  // Debounce search
  const debounceRef = React.useRef<any>(null);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  const { data, isLoading } = useProductsOnTheWayList(
    {
      page,
      limit: ITEMS_PER_PAGE,
      sortBy: 'stock_desc',
      search: debouncedSearch,
      hide_arrived: true,
      has_remaining_stock: true,
    },
    undefined,
  );

  const results = useMemo(() => data?.results || [], [data]);
  const totalResults = data?.totalResults || 0;
  const totalPages = data?.totalPages || 1;

  if (permLoading) return null;
  if (!hasPermission) return null;

  const renderSkeleton = () => (
    <div className="col-12">
      <div className="card card-xl-stretch mb-xl-8">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">Yoldaki Ürünler</span>
            <span className="text-muted mt-1 fw-bold fs-7">Yükleniyor...</span>
          </h3>
        </div>
        <div className="card-body py-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="d-flex align-items-center mb-5">
              <div className="placeholder-glow flex-grow-1">
                <span className="placeholder col-6 bg-secondary mb-2 d-block"></span>
                <span className="placeholder col-4 bg-secondary d-block"></span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (isLoading && page === 1 && !debouncedSearch) return renderSkeleton();

  return (
    <div className="col-12">
      <div className="card card-xl-stretch mb-xl-8">
        {/* Header */}
        <div className="card-header border-0 pt-5 pb-2" style={{ backgroundColor: 'rgba(113, 57, 200, 0.15)' }}>
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">
              <i className="bi bi-truck me-2" style={{ color: '#7239C0' }}></i>
              Yoldaki Ürünler
            </span>
            <span className="text-muted mt-1 fw-bold fs-7">Stoğa girecek olan {totalResults} ürün</span>
          </h3>
          <div className="card-toolbar d-flex align-items-center gap-2">
            {/* Search */}
            <div className="position-relative">
              <span className="svg-icon svg-icon-3 position-absolute ms-3" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
              </span>
              <input
                type="text"
                className="form-control form-control-sm form-control-solid ps-10"
                placeholder="Ürün, ürün kodu, müşteri ara..."
                value={search}
                onChange={handleSearchChange}
                style={{ width: '200px' }}
              />
            </div>
            <Link to="/products-on-the-way" className="btn btn-sm fw-bold" style={{ backgroundColor: 'rgba(113, 57, 200, 0.1)', color: '#7239C0' }}>
              Tümünü Gör
            </Link>
          </div>
        </div>

        {/* Body */}
        <div className="card-body py-0">
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3">
              <thead>
                <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                  <th className="min-w-200px">Ürün</th>
                  <th className="min-w-120px">Müşteri</th>
                  <th className="min-w-80px text-center">Sipariş Tarihi</th>
                  <th className="min-w-60px text-center">Adet</th>
                  <th className="min-w-60px text-center">Satılan</th>
                  <th className="min-w-60px text-center">Stok</th>
                  <th className="min-w-80px text-center">Durum</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={7}>
                        <div className="placeholder-glow">
                          <span className="placeholder col-12 bg-secondary"></span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : results.length > 0 ? (
                  results.map((item: any) => (
                    <tr key={item.id}>
                      <td>
                        <div className="fw-semibold text-gray-800 fs-7">{item.product_name}</div>
                        {item.product_code && <span className="text-muted fs-8">{item.product_code}</span>}
                      </td>
                      <td>
                        <span className="text-gray-700 fs-7">{item.customer_name || '-'}</span>
                      </td>
                      <td className="text-center">
                        <span className="fs-7">{item.order_date ? moment(item.order_date).locale('tr').format('DD.MM.YYYY') : '-'}</span>
                      </td>
                      <td className="text-center">
                        <span className="badge badge-light-primary">{item.quantity || '0'}</span>
                      </td>
                      <td className="text-center">
                        <span className="badge badge-light-info">{item.sales_quantity || '0'}</span>
                      </td>
                      <td className="text-center">
                        <span className={`badge ${(item.stock || 0) > 0 ? 'badge-light-success' : (item.stock || 0) < 0 ? 'badge-light-danger' : 'badge-light'}`}>
                          {item.stock || '0'}
                        </span>
                      </td>
                      <td className="text-center">
                        {item.status === 'geldi' ? <span className="badge badge-light-success">Geldi</span> : <span className="badge badge-light-warning">Yolda</span>}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-10">
                      <div className="d-flex flex-column align-items-center">
                        <i className="bi bi-truck text-muted fs-2x mb-3"></i>
                        <span className="text-muted fs-6">{debouncedSearch ? 'Aramanızla eşleşen ürün bulunamadı' : 'Yoldaki ürün bulunamadı'}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center py-4">
              <span className="text-muted fs-7">
                {(page - 1) * ITEMS_PER_PAGE + 1}-{Math.min(page * ITEMS_PER_PAGE, totalResults)} / {totalResults}
              </span>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    <i className="bi bi-chevron-left fs-8"></i>
                  </button>
                </li>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(pageNum)}>
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                    <i className="bi bi-chevron-right fs-8"></i>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsOnTheWayWidget;
